import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

/**
 * Template coordinate maps for Vehicle (Tractor) and Trailer PDFs.
 * PDF coordinates: origin bottom-left, units in points (1/72 inch).
 */
const TEMPLATE_COORDINATES = {
  vehicle: {
    carrier_name: { x: 83, y: 569, fontSize: 9, maxWidth: 220 },
    carrier_address: { x: 82, y: 544, fontSize: 9, maxWidth: 260 },
    carrier_city: { x: 82, y: 519, fontSize: 9, maxWidth: 60 },
    carrier_state: { x: 163, y: 519, fontSize: 9, maxWidth: 70 },
    carrier_zip: { x: 239, y: 519, fontSize: 9, maxWidth: 80 },
    unit_number: { x: 312, y: 544, fontSize: 9, maxWidth: 120 },
    vin: { x: 312, y: 519, fontSize: 9, maxWidth: 140 },
    inspection_name: { x: 311, y: 494, fontSize: 9, maxWidth: 200 },
    inspection_date: { x: 466, y: 571, fontSize: 9, maxWidth: 90 },
    certified_checkmark: { x: 306, y: 609, fontSize: 10 },
    inspection_site: { x: 82, y: 493, fontSize: 9, maxWidth: 260 },
    vehicle_type_tractor: { x: 450, y: 988, fontSize: 10 },
    vehicle_type_trailer: { x: 510, y: 988, fontSize: 10 },
  },
  trailer: {
    carrier_name: { x: 82, y: 571, fontSize: 9, maxWidth: 220 },
    carrier_address: { x: 82, y: 545, fontSize: 9, maxWidth: 260 },
    carrier_city: { x: 82, y: 520, fontSize: 9, maxWidth: 60 },
    carrier_state: { x: 164, y: 523, fontSize: 9, maxWidth: 70 },
    carrier_zip: { x: 238, y: 520, fontSize: 9, maxWidth: 80 },
    unit_number: { x: 313, y: 544, fontSize: 9, maxWidth: 120 },
    vin: { x: 312, y: 520, fontSize: 9, maxWidth: 140 },
    inspection_name: { x: 312, y: 497, fontSize: 9, maxWidth: 200 },
    inspection_date: { x: 464, y: 571, fontSize: 9, maxWidth: 90 },
    certified_checkmark: { x: 306, y: 609, fontSize: 10 },
    inspection_site: { x: 82, y: 496, fontSize: 9, maxWidth: 260 },
  },
};

/**
 * Wraps text to fit within a maximum width.
 * @param {string} text - Text to wrap
 * @param {number} maxWidth - Maximum width in points
 * @param {PDFFont} font - Embedded font
 * @param {number} fontSize - Font size
 * @returns {string[]} Array of wrapped lines
 */
function wrapText(text, maxWidth, font, fontSize) {
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(testLine, fontSize);
    if (width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

/**
 * Truncates text to fit within a maximum width with ellipsis.
 * @param {string} text - Text to truncate
 * @param {number} maxWidth - Maximum width in points
 * @param {PDFFont} font - Embedded font
 * @param {number} fontSize - Font size
 * @returns {string} Truncated text
 */
function truncateToFit(text, maxWidth, font, fontSize) {
  if (font.widthOfTextAtSize(text, fontSize) <= maxWidth) return text;
  let truncated = text;
  while (
    truncated.length > 1 &&
    font.widthOfTextAtSize(truncated + "...", fontSize) > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + "...";
}

/**
 * Generates a DOT Inspection PDF by filling in the template.
 * @param {Object} fields - Inspection fields
 * @param {string} fields.type - "Vehicle" or "Trailer"
 * @param {string} fields.carrier_name - Carrier/Company name
 * @param {string} fields.carrier_address - Carrier address
 * @param {string} fields.carrier_city - City
 * @param {string} fields.carrier_state - State (2-letter code)
 * @param {string} fields.carrier_zip - ZIP code
 * @param {string} fields.unit_number - Unit number
 * @param {string} fields.vin - Vehicle Identification Number
 * @param {string} fields.inspection_name - Inspector name
 * @param {string} fields.inspection_date - Date in YYYY-MM-DD format
 * @param {string} fields.inspection_site - Inspection site name
 * @param {boolean} fields.certified - Whether inspection is certified
 * @returns {Promise<Uint8Array>} PDF bytes
 */
export async function generateDotInspectionPdf(fields) {
  // Determine template type based on equipment type
  const templateType = fields.type === "Trailer" ? "trailer" : "vehicle";
  const templatePath = `/files/dot-inspection/${templateType}_template.pdf`;

  // Fetch the PDF template
  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error(`Failed to load template: ${templatePath}`);
  }
  const templateBytes = await response.arrayBuffer();

  // Load the PDF document
  const pdfDoc = await PDFDocument.load(templateBytes);
  const pages = pdfDoc.getPages();
  const page = pages[0];

  // Embed font
  const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Get coordinates for this template type
  const fieldMap = TEMPLATE_COORDINATES[templateType];

  const lineHeight = 12;
  const yOffset = 80;
  const leftOffset = 3;
  const rightFieldsLeftOffset = 1;

  // Draw carrier name (may need wrapping)
  const carrierPos = fieldMap.carrier_name;
  const carrierFontSize = carrierPos.fontSize || 9;
  const carrierMaxWidth = carrierPos.maxWidth || 180;
  const carrierLines = wrapText(
    fields.carrier_name,
    carrierMaxWidth,
    font,
    carrierFontSize
  );
  carrierLines.slice(0, 3).forEach((line, i) => {
    page.drawText(line, {
      x: carrierPos.x - leftOffset,
      y: carrierPos.y + yOffset - i * lineHeight,
      size: carrierFontSize,
      font,
      color: rgb(0, 0, 0),
    });
  });

  // Draw carrier address
  const addrPos = fieldMap.carrier_address;
  const addrFontSize = addrPos.fontSize || 9;
  const addrLines = wrapText(
    fields.carrier_address,
    addrPos.maxWidth || 180,
    font,
    addrFontSize
  );
  addrLines.slice(0, 2).forEach((line, i) => {
    page.drawText(line, {
      x: addrPos.x - leftOffset,
      y: addrPos.y + yOffset - i * lineHeight,
      size: addrFontSize,
      font,
      color: rgb(0, 0, 0),
    });
  });

  // Draw city, state, zip
  const cityPos = fieldMap.carrier_city;
  const statePos = fieldMap.carrier_state;
  const zipPos = fieldMap.carrier_zip;
  const rowFontSize = cityPos.fontSize || 9;
  const rowY = cityPos.y + yOffset + 3;

  const cityText = truncateToFit(
    fields.carrier_city,
    cityPos.maxWidth || 100,
    font,
    rowFontSize
  );
  page.drawText(cityText, {
    x: cityPos.x - leftOffset,
    y: rowY,
    size: rowFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  page.drawText(fields.carrier_state, {
    x: statePos.x - leftOffset,
    y: rowY,
    size: rowFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  page.drawText(fields.carrier_zip, {
    x: zipPos.x - leftOffset,
    y: rowY,
    size: rowFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  // Draw unit number
  const unitPos = fieldMap.unit_number;
  const unitFontSize = unitPos.fontSize || 9;
  page.drawText(fields.unit_number, {
    x: unitPos.x - rightFieldsLeftOffset,
    y: unitPos.y + yOffset,
    size: unitFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  // Draw VIN
  const vinPos = fieldMap.vin;
  const vinFontSize = vinPos.fontSize || 9;
  const vinText = truncateToFit(
    fields.vin,
    vinPos.maxWidth || 150,
    font,
    vinFontSize
  );
  page.drawText(vinText, {
    x: vinPos.x - rightFieldsLeftOffset,
    y: vinPos.y + yOffset + 3,
    size: vinFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  // Draw inspector name
  const inspPos = fieldMap.inspection_name;
  const inspFontSize = inspPos.fontSize || 9;
  const inspText = truncateToFit(
    fields.inspection_name,
    inspPos.maxWidth || 180,
    font,
    inspFontSize
  );
  page.drawText(inspText, {
    x: inspPos.x - rightFieldsLeftOffset,
    y: inspPos.y + yOffset + 5,
    size: inspFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  // Draw inspection date (format as MM/DD/YY)
  const datePos = fieldMap.inspection_date;
  const dateFontSize = datePos.fontSize || 9;
  const dateParts = fields.inspection_date.split("-");
  let dateFormatted = fields.inspection_date;
  if (dateParts.length === 3) {
    const [year, month, day] = dateParts;
    dateFormatted = `${month}/${day}/${year.slice(-2)}`;
  }
  page.drawText(dateFormatted, {
    x: datePos.x - rightFieldsLeftOffset,
    y: datePos.y + yOffset - 2,
    size: dateFontSize,
    font,
    color: rgb(0, 0, 0),
  });

  // Draw inspection site
  if (fields.inspection_site) {
    const sitePos = fieldMap.inspection_site;
    const siteFontSize = sitePos.fontSize || 9;
    const siteText = truncateToFit(
      fields.inspection_site,
      sitePos.maxWidth || 260,
      font,
      siteFontSize
    );
    page.drawText(siteText, {
      x: sitePos.x - leftOffset,
      y: sitePos.y + yOffset + 5,
      size: siteFontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }

  // Save and return the PDF bytes
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

/**
 * Downloads the generated PDF to the user's device.
 * @param {Uint8Array} pdfBytes - PDF bytes
 * @param {string} filename - Filename for download
 */
export function downloadPdf(pdfBytes, filename) {
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
